import { Boards, Clips, ShortUrl } from '@air/api';
import { ShortUrlObjectType } from '@air/api/types';
import { callAirApi } from '@air/utils-api';

import { type WorkspaceAgentComposerSelectionDragItem } from '~/components/v2/components/workspace/workspace-agent-panel/drag/workspaceAgentComposerSelectionDragPayload';
import { toWorkspaceAgentAssetMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentAssetMentionItem';
import { toWorkspaceAgentBoardMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentBoardMentionItem';
import { toWorkspaceAgentPillNodeAttrs } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionSources';
import { type WorkspaceAgentPillNodeAttrs } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';
import {
  createAllContentAssetRoute,
  createBoardRoute,
  createSavedSearchRoute,
  createShortUrlRoute,
  LEGACY_SAVED_FILTER_PATH_PREFIX,
} from '~/constants/routes';
import { isSafeRedirectUrl } from '~/utils/auth/isSafeRedirectUrl';
import {
  getBoardIdFromPath,
  getClipIdFromPath,
  getPrivateCanvasIdFromPath,
  getShortIdFromPath,
} from '~/utils/PathUtils';

export type WorkspaceAgentComposerReferenceUrl =
  | { boardId: string | null; clipId: string; shareContextShortId?: string; type: 'asset' }
  | { boardId: string; shareContextShortId?: string; type: 'board' }
  | { shareShortId: string; type: 'shareBoardRoot' }
  | { shareShortId: string; type: 'shareClipRoot' };

export type WorkspaceAgentComposerTextSegment = { kind: 'text'; text: string } | { kind: 'url'; url: string };

type WorkspaceAgentComposerReferenceUrlCandidateMatch = { end: number; index: number; url: string };

/** Prefixes from `~/constants/routes` builders with an empty id (everything before the id segment). */
const WORKSPACE_AGENT_COMPOSER_BOARD_PATH_PREFIX = createBoardRoute('');
const WORKSPACE_AGENT_COMPOSER_ALL_CONTENT_ASSET_PATH_PREFIX = createAllContentAssetRoute('');
const WORKSPACE_AGENT_COMPOSER_SAVED_SEARCH_PATH_PREFIX = createSavedSearchRoute('');

const WORKSPACE_AGENT_COMPOSER_SHARE_PATH_PREFIX = createShortUrlRoute('');

const WORKSPACE_AGENT_COMPOSER_REFERENCE_PATH_PREFIXES = [
  WORKSPACE_AGENT_COMPOSER_BOARD_PATH_PREFIX,
  WORKSPACE_AGENT_COMPOSER_ALL_CONTENT_ASSET_PATH_PREFIX,
  WORKSPACE_AGENT_COMPOSER_SAVED_SEARCH_PATH_PREFIX,
  LEGACY_SAVED_FILTER_PATH_PREFIX,
  WORKSPACE_AGENT_COMPOSER_SHARE_PATH_PREFIX,
] as const;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

const buildAbsoluteReferenceUrlPattern = (pathPrefix: string): RegExp =>
  new RegExp(String.raw`https?:\/\/[^\s]+${escapeRegExp(pathPrefix)}[^\s]*`, 'gi');

const buildProtocolRelativeReferenceUrlPattern = (pathPrefix: string): RegExp =>
  new RegExp(String.raw`\/\/[^\s]+${escapeRegExp(pathPrefix)}[^\s]*`, 'gi');

const buildRelativeReferenceUrlPattern = (pathPrefix: string): RegExp =>
  new RegExp(String.raw`${escapeRegExp(pathPrefix)}[^\s]*`, 'gi');

/** One pattern per path prefix and URL shape (absolute, protocol-relative, relative). */
const WORKSPACE_AGENT_COMPOSER_REFERENCE_URL_CANDIDATE_PATTERNS: readonly RegExp[] =
  WORKSPACE_AGENT_COMPOSER_REFERENCE_PATH_PREFIXES.flatMap((pathPrefix) => [
    buildAbsoluteReferenceUrlPattern(pathPrefix),
    buildProtocolRelativeReferenceUrlPattern(pathPrefix),
    buildRelativeReferenceUrlPattern(pathPrefix),
  ]);

const collectWorkspaceAgentComposerReferenceUrlCandidates = (
  text: string,
): WorkspaceAgentComposerReferenceUrlCandidateMatch[] => {
  const matches: WorkspaceAgentComposerReferenceUrlCandidateMatch[] = [];

  for (const pattern of WORKSPACE_AGENT_COMPOSER_REFERENCE_URL_CANDIDATE_PATTERNS) {
    pattern.lastIndex = 0;

    for (const match of text.matchAll(pattern)) {
      const index = match.index ?? 0;
      const url = match[0];

      matches.push({ end: index + url.length, index, url });
    }
  }

  matches.sort((left, right) => left.index - right.index || right.end - left.end);

  const nonOverlapping: WorkspaceAgentComposerReferenceUrlCandidateMatch[] = [];
  let lastEnd = 0;

  for (const candidate of matches) {
    if (candidate.index < lastEnd) {
      continue;
    }

    nonOverlapping.push(candidate);
    lastEnd = candidate.end;
  }

  return nonOverlapping;
};

const WORKSPACE_AGENT_COMPOSER_REFERENCE_BOARD_PARAMS = {
  includeCoverImage: true,
  numThumbnails: 1,
} as const;

const trimTrailingUrlPunctuation = (url: string): string => url.replace(/[),.;:!?\]]+$/, '');

const getWorkspaceAgentComposerReferenceUrlParseBase = (): string | undefined => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return undefined;
};

const parseWorkspaceAgentComposerReferencePastedUrl = (trimmed: string): URL | null => {
  const parseBase = getWorkspaceAgentComposerReferenceUrlParseBase();

  try {
    return new URL(trimmed);
  } catch {
    if (!parseBase) {
      return null;
    }

    try {
      return new URL(trimmed, parseBase);
    } catch {
      return null;
    }
  }
};

/** Same rule as `isSafeRedirectUrl`: only URLs for this app origin become composer references. */
const isWorkspaceAgentComposerReferenceUrlForCurrentApp = (rawUrl: string): boolean => {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return false;
  }

  return isSafeRedirectUrl(rawUrl);
};

const parseWorkspaceAgentComposerShareLinkReferenceFromPathname = (
  pathname: string,
): WorkspaceAgentComposerReferenceUrl | null => {
  const shortId = getShortIdFromPath(pathname);

  if (!shortId) {
    return null;
  }

  const shortIdTypePrefix = shortId[0]?.toLowerCase();

  if (shortIdTypePrefix === 'v') {
    return null;
  }

  const boardId = getBoardIdFromPath(pathname);
  const clipId = getClipIdFromPath(pathname);

  if (shortIdTypePrefix === 'c' || shortIdTypePrefix === 'a') {
    if (boardId) {
      return null;
    }

    if (clipId) {
      return { boardId: null, clipId, shareContextShortId: shortId, type: 'asset' };
    }

    return { shareShortId: shortId, type: 'shareClipRoot' };
  }

  if (shortIdTypePrefix === 'm') {
    if (clipId) {
      return { boardId: boardId ?? null, clipId, shareContextShortId: shortId, type: 'asset' };
    }

    if (boardId) {
      return { boardId, shareContextShortId: shortId, type: 'board' };
    }

    return null;
  }

  if (shortIdTypePrefix === 'b') {
    if (clipId) {
      return { boardId: boardId ?? null, clipId, shareContextShortId: shortId, type: 'asset' };
    }

    if (boardId) {
      return { boardId, shareContextShortId: shortId, type: 'board' };
    }

    return { shareShortId: shortId, type: 'shareBoardRoot' };
  }

  return null;
};

const parseWorkspaceAgentComposerReferenceFromPathname = (
  pathname: string,
): WorkspaceAgentComposerReferenceUrl | null => {
  if (getShortIdFromPath(pathname)) {
    return parseWorkspaceAgentComposerShareLinkReferenceFromPathname(pathname);
  }

  const clipId = getClipIdFromPath(pathname);

  if (clipId) {
    return {
      boardId: getBoardIdFromPath(pathname) ?? null,
      clipId,
      type: 'asset',
    };
  }

  if (getPrivateCanvasIdFromPath(pathname) || /\/canvas(?:\/|$)/i.test(pathname)) {
    return null;
  }

  const boardId = getBoardIdFromPath(pathname);

  if (boardId) {
    return { boardId, type: 'board' };
  }

  return null;
};

/**
 * Parses a pasted Air app board or asset URL into the ids a composer pill needs. Returns `null` for
 * unrelated hosts, collection-only share links, canvas share links, and paths with no resolvable reference.
 */
export const parseWorkspaceAgentComposerReferenceUrl = (raw: string): WorkspaceAgentComposerReferenceUrl | null => {
  const trimmed = trimTrailingUrlPunctuation(raw.trim());

  if (!trimmed) {
    return null;
  }

  const url = parseWorkspaceAgentComposerReferencePastedUrl(trimmed);

  if (!url) {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  if (!isWorkspaceAgentComposerReferenceUrlForCurrentApp(trimmed)) {
    return null;
  }

  return parseWorkspaceAgentComposerReferenceFromPathname(url.pathname);
};

/** Splits plain text into prose and Air reference URLs accepted by {@link parseWorkspaceAgentComposerReferenceUrl}. */
export const splitWorkspaceAgentComposerTextByReferenceUrls = (text: string): WorkspaceAgentComposerTextSegment[] => {
  const segments: WorkspaceAgentComposerTextSegment[] = [];
  let lastIndex = 0;

  for (const { index: matchIndex, url: matchedUrl } of collectWorkspaceAgentComposerReferenceUrlCandidates(text)) {
    if (matchIndex > lastIndex) {
      segments.push({ kind: 'text', text: text.slice(lastIndex, matchIndex) });
    }

    if (parseWorkspaceAgentComposerReferenceUrl(matchedUrl)) {
      const trimmedUrl = trimTrailingUrlPunctuation(matchedUrl);
      const trailingText = matchedUrl.slice(trimmedUrl.length);

      segments.push({ kind: 'url', url: trimmedUrl });

      if (trailingText) {
        segments.push({ kind: 'text', text: trailingText });
      }
    } else {
      segments.push({ kind: 'text', text: matchedUrl });
    }

    lastIndex = matchIndex + matchedUrl.length;
  }

  if (lastIndex < text.length) {
    segments.push({ kind: 'text', text: text.slice(lastIndex) });
  }

  if (segments.length === 0 && text.length > 0) {
    segments.push({ kind: 'text', text });
  }

  return segments;
};

export const workspaceAgentComposerTextContainsReferenceUrl = (text: string): boolean =>
  splitWorkspaceAgentComposerTextByReferenceUrls(text).some((segment) => segment.kind === 'url');

type ShortUrlGetByShortIdResponse = Awaited<ReturnType<typeof ShortUrl.getByShortId>>;

const isWorkspaceAgentComposerReferenceInCurrentWorkspace = (
  entityWorkspaceId: string | null | undefined,
  workspaceId: string,
): boolean => entityWorkspaceId === workspaceId;

const getWorkspaceIdFromShortUrlGetByShortIdResponse = (response: ShortUrlGetByShortIdResponse): string | undefined => {
  if (response.type === ShortUrlObjectType.board) {
    return response.data.workspace.id;
  }

  if (response.type === ShortUrlObjectType.clip || response.type === ShortUrlObjectType.asset) {
    return response.data.workspaceId;
  }

  if (response.type === ShortUrlObjectType.collection) {
    return response.data.workspaceId ?? response.data.workspace.id;
  }

  return undefined;
};

const isShareContextShortIdInCurrentWorkspace = async (
  shareContextShortId: string,
  workspaceId: string,
): Promise<boolean> => {
  try {
    const response = await callAirApi(() => ShortUrl.getByShortId(shareContextShortId));
    const shareWorkspaceId = getWorkspaceIdFromShortUrlGetByShortIdResponse(response);

    return isWorkspaceAgentComposerReferenceInCurrentWorkspace(shareWorkspaceId, workspaceId);
  } catch {
    return false;
  }
};

const resolveWorkspaceAgentComposerAssetClip = async ({
  clipId,
  workspaceId,
}: {
  clipId: string;
  workspaceId: string;
}): Promise<WorkspaceAgentPillNodeAttrs | null> => {
  const clips = await callAirApi(() =>
    Clips.listByIds({
      clipIds: [clipId],
      workspaceId,
    }),
  );
  const clip = clips.find((entry) => entry.id === clipId) ?? clips[0];

  if (!clip || !isWorkspaceAgentComposerReferenceInCurrentWorkspace(clip.workspaceId, workspaceId)) {
    return null;
  }

  return toWorkspaceAgentPillNodeAttrs(toWorkspaceAgentAssetMentionItem(clip));
};

/** Workspace-scoped resolve for gallery selection drops — same trust boundary as pasted Air URLs. */
export const resolveWorkspaceAgentComposerSelectionDragItem = async ({
  item,
  workspaceId,
}: {
  item: WorkspaceAgentComposerSelectionDragItem;
  workspaceId: string;
}): Promise<WorkspaceAgentPillNodeAttrs | null> => {
  if (item.kind === 'board') {
    try {
      const board = await callAirApi(() =>
        Boards.getWithNoWorkspaceId({
          ...WORKSPACE_AGENT_COMPOSER_REFERENCE_BOARD_PARAMS,
          boardId: item.boardId,
        }),
      );

      if (!isWorkspaceAgentComposerReferenceInCurrentWorkspace(board.workspaceId, workspaceId)) {
        return null;
      }

      return toWorkspaceAgentPillNodeAttrs(toWorkspaceAgentBoardMentionItem(board));
    } catch {
      return null;
    }
  }

  try {
    const resolvedAttrs = await resolveWorkspaceAgentComposerAssetClip({
      clipId: item.versionId,
      workspaceId,
    });

    if (!resolvedAttrs || resolvedAttrs.assetId !== item.assetId) {
      return null;
    }

    return resolvedAttrs;
  } catch {
    return null;
  }
};

/** Loads board or clip metadata so a pasted URL can become a fully populated composer pill. */
export const resolveWorkspaceAgentComposerReferenceUrl = async ({
  reference,
  workspaceId,
}: {
  reference: WorkspaceAgentComposerReferenceUrl;
  workspaceId: string;
}): Promise<WorkspaceAgentPillNodeAttrs | null> => {
  if (reference.type === 'shareBoardRoot') {
    try {
      const response = await callAirApi(() => ShortUrl.getByShortId(reference.shareShortId));

      if (response.type !== ShortUrlObjectType.board) {
        return null;
      }

      const shareWorkspaceId = getWorkspaceIdFromShortUrlGetByShortIdResponse(response);

      if (!isWorkspaceAgentComposerReferenceInCurrentWorkspace(shareWorkspaceId, workspaceId)) {
        return null;
      }

      return toWorkspaceAgentPillNodeAttrs(toWorkspaceAgentBoardMentionItem(response.data.board));
    } catch {
      return null;
    }
  }

  if (reference.type === 'shareClipRoot') {
    try {
      const response = await callAirApi(() => ShortUrl.getByShortId(reference.shareShortId));

      if (response.type !== ShortUrlObjectType.clip && response.type !== ShortUrlObjectType.asset) {
        return null;
      }

      const shareWorkspaceId = getWorkspaceIdFromShortUrlGetByShortIdResponse(response);

      if (!isWorkspaceAgentComposerReferenceInCurrentWorkspace(shareWorkspaceId, workspaceId)) {
        return null;
      }

      const clips = await callAirApi(() =>
        Clips.listByIds({
          clipIds: [response.data.id],
          workspaceId,
        }),
      );
      const clip = clips[0] ?? response.data;

      if (!isWorkspaceAgentComposerReferenceInCurrentWorkspace(clip.workspaceId, workspaceId)) {
        return null;
      }

      return toWorkspaceAgentPillNodeAttrs(toWorkspaceAgentAssetMentionItem(clip));
    } catch {
      return null;
    }
  }

  if (reference.type === 'board') {
    try {
      if (
        reference.shareContextShortId &&
        !(await isShareContextShortIdInCurrentWorkspace(reference.shareContextShortId, workspaceId))
      ) {
        return null;
      }

      const board = await callAirApi(() =>
        Boards.getWithNoWorkspaceId({
          ...WORKSPACE_AGENT_COMPOSER_REFERENCE_BOARD_PARAMS,
          boardId: reference.boardId,
        }),
      );

      if (!isWorkspaceAgentComposerReferenceInCurrentWorkspace(board.workspaceId, workspaceId)) {
        return null;
      }

      return toWorkspaceAgentPillNodeAttrs(toWorkspaceAgentBoardMentionItem(board));
    } catch {
      // Best-effort resolution — lookup failure should fall back to pasting the raw URL.
      return null;
    }
  }

  try {
    if (
      reference.shareContextShortId &&
      !(await isShareContextShortIdInCurrentWorkspace(reference.shareContextShortId, workspaceId))
    ) {
      return null;
    }

    return resolveWorkspaceAgentComposerAssetClip({ clipId: reference.clipId, workspaceId });
  } catch {
    // Best-effort resolution — lookup failure should fall back to pasting the raw URL.
    return null;
  }
};
